import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Swords, Check, X } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";

/**
 * BattleChallengeBanner — Phase 4B. Mounted at the app shell so wherever
 * you are (Feed, Nearby, Profile, DMs), if another host challenges you
 * to a Live Battle you get an instant Accept/Decline card.
 *
 * Fast path: subscribes to a user-scoped WS channel on the opponent's
 * home room (their `host_stream_id`). Fallback: 15s poll of
 * `GET /battles/incoming`. Both paths are additive; either alone works.
 *
 * State machine:
 *   dismissed → check /battles/incoming on mount + WS message → show
 *   accept → navigate to /live?s={host_stream_id} and let BattleBanner
 *            take over (already listens for battle:accept + score frames)
 *   decline → POST /respond {accept:false}, hide.
 */
export default function BattleChallengeBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [incoming, setIncoming] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/battles/incoming");
      const first = data?.battles?.[0];
      setIncoming(first || null);
    } catch { /* transient */ }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    load();
    // 15s safety-net poll — WS drives the fast path below.
    const t = setInterval(load, 15_000);
    // Try opening a WS to /api/ws/users/{me} so we hear battle:challenge
    // regardless of what room we're in.
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = backend.replace(/^http/, "ws") + `/api/ws/users/${user.id}`;
    let ws;
    try {
      ws = new WebSocket(wsUrl);
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg?.channel === "battle:challenge" && msg?.opponent_id === user.id) {
            load();
          }
          if (msg?.channel === "battle:decline" || msg?.channel === "battle:accept") {
            setIncoming(null);
          }
        } catch { /* ignore malformed */ }
      };
    } catch { /* transient — poll covers */ }
    return () => { clearInterval(t); try { ws?.close(); } catch { /* ignore */ } };
  }, [user, load]);

  const accept = async () => {
    if (!incoming || busy) return;
    setBusy(true);
    try {
      await api.post(`/battles/${incoming.id}/respond`, { accept: true });
      toast.success("Battle accepted!");
      const sid = incoming.opponent_stream_id;   // caller was on THIS stream
      setIncoming(null);
      if (sid) navigate(`/live?s=${sid}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not accept battle.");
    } finally { setBusy(false); }
  };

  const decline = async () => {
    if (!incoming || busy) return;
    setBusy(true);
    try {
      await api.post(`/battles/${incoming.id}/respond`, { accept: false });
      setIncoming(null);
    } catch { /* silent */ }
    finally { setBusy(false); }
  };

  if (!incoming) return null;

  return (
    <div
      data-testid="battle-challenge-banner"
      className="fixed left-1/2 -translate-x-1/2 top-4 z-[95] w-[92vw] max-w-md bg-gradient-to-br from-rose-600/95 to-fuchsia-700/95 backdrop-blur-md border border-rose-300/30 rounded-2xl shadow-2xl shadow-rose-500/30 px-3 py-2.5 flex items-center gap-3"
    >
      <Avatar className="w-10 h-10 border border-white/30 shrink-0">
        <AvatarImage src={incoming?.host?.avatar_url} />
        <AvatarFallback className="bg-black/30 text-white text-xs">
          {(incoming?.host?.display_name || "?")[0]?.toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest text-white/80 flex items-center gap-1">
          <Swords size={11} /> Battle challenge
        </div>
        <div className="text-white font-semibold text-sm leading-tight truncate">
          {incoming?.host?.display_name || "someone"} wants to battle
        </div>
        <div className="text-[11px] text-white/80">
          {Math.round((incoming?.duration_seconds || 300) / 60)} min
        </div>
      </div>
      <button
        type="button"
        data-testid="battle-decline"
        onClick={decline}
        disabled={busy}
        className="w-9 h-9 rounded-full bg-white/10 border border-white/20 text-white grid place-items-center hover:bg-white/20 disabled:opacity-40"
        aria-label="Decline"
      >
        <X size={16} />
      </button>
      <button
        type="button"
        data-testid="battle-accept"
        onClick={accept}
        disabled={busy}
        className="h-9 px-3 rounded-full bg-white text-rose-700 font-semibold text-sm flex items-center gap-1 hover:bg-white/90 disabled:opacity-40"
      >
        <Check size={15} /> Accept
      </button>
    </div>
  );
}
